import { Component, Inject, computed, effect, signal, DOCUMENT, ChangeDetectionStrategy } from "@angular/core";
import { RouterModule } from "@angular/router";
import { FinzzleAuthService } from "@finzzle/auth";
import { OuiButtonModule } from "omnium-ui/button";
import { OuiErrorPageComponent } from "omnium-ui/error-page";
import {
  OuiLayoutModule,
  OuiNavigationPanel,
  SocialMediaLinkGroup,
} from "omnium-ui/layout";
import { OuiTagModule } from "omnium-ui/tag";
import { MenuNode } from "omnium-ui/shared";
import { appRoutePaths } from "./app.paths";
import { ApplicationStatus } from "./core/models/api.models";
import { ActionAuthorizationService } from "./core/auth/action-authorization.service";
import { DoublonsBusterApiService } from "./core/services/doublons-buster-api.service";

@Component({
  selector: "dbw-root",
  imports: [
    RouterModule,
    OuiLayoutModule,
    OuiNavigationPanel,
    OuiButtonModule,
    OuiTagModule,
    OuiErrorPageComponent,
  ],
  templateUrl: "./app.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./app.component.scss"],
})
export class AppComponent {
  readonly appLabel = "DoublonsBuster";
  readonly logoUrl = "assets/doublons-buster-logo.png";
  readonly footerLinks: MenuNode[] = [];
  readonly footerSocialMedia: SocialMediaLinkGroup[] = [];
  readonly userMenu: MenuNode[] = [
    {
      name: "Déconnexion",
      action: () => this.logout(),
    },
  ];
  readonly navigationMenu: MenuNode[] = [
    {
      name: 'Atlas',
      icon: 'database',
      children: [
        {
          id: 'recherche-investisseur',
          name: 'Recherche Investisseur',
          routeLink: appRoutePaths.atlas.rechercheInvestisseur
        },
        {
          id: 'recherche-consultant',
          name: 'Recherche Consultant',
          routeLink: appRoutePaths.atlas.rechercheConsultant
        },
        {
          id: 'doublons_ariane',
          name: 'Doublons Ariane',
          routeLink: appRoutePaths.atlas.doublonsAriane
        },
        {
          id: 'doublons_personne_physique',
          name: 'Doublons personne physique',
          routeLink: appRoutePaths.atlas.doublonsPersonnePhysique
        },
        {
          id: 'relations_inter_consultants',
          name: 'Relations inter-consultants',
          routeLink: appRoutePaths.atlas.relationsInterConsultants
        },
        {
          id: 'bouquet_service',
          name: 'Bouquet de service',
          routeLink: appRoutePaths.atlas.bouquetService
        }
      ]
    },
    {
      name: "Minotaure",
      icon: "flare",
      children: [
        {
          id: "report",
          name: "Reporting",
          routeLink: appRoutePaths.minotaure.reporting,
        },
        {
          id: "recueil_inter_consultants",
          name: "Recueil inter-consultants",
          routeLink: appRoutePaths.minotaure.recueilInterConsultants,
        },
        {
          id: "batch",
          name: "Batch",
          routeLink: appRoutePaths.minotaure.batch,
        },
      ],
    },
    {
      name: 'Contrôle de gestion',
      icon: 'request_quote',
      children: [
        {
          id: 'couts_ex_post',
          name: 'Coûts ex-post',
          routeLink: appRoutePaths.controleGestion.coutsExPost
        }
      ]
    },
    {
      name: 'Technique',
      icon: 'settings',
      children: [
        {
          id: 'technical_caches',
          name: 'Caches',
          routeLink: appRoutePaths.technical.caches
        },
        {
          id: 'technical_purge_jobs',
          name: 'Demandes de purge',
          routeLink: appRoutePaths.technical.purgeJobs
        }
      ]
    },
    {
      name: 'Aramis',
      icon: 'account_balance',
      children: [
        {
          id: 'aramis_integration_april',
          name: 'Intégration APRIL',
          routeLink: appRoutePaths.aramis.integrationApril
        }
      ]
    },
    {
      name: 'Proxeema',
      icon: 'draw',
      children: [
        {
          id: 'proxeema_investor_signatures',
          name: 'Signatures investisseur',
          routeLink: appRoutePaths.proxeema.signaturesInvestisseur
        },
        {
          id: 'proxeema_universign_transactions',
          name: 'Transactions Universign',
          routeLink: appRoutePaths.proxeema.transactionsUniversign
        },
        {
          id: 'proxeema_signature_anomalies',
          name: 'Anomalies de signature',
          routeLink: appRoutePaths.proxeema.anomaliesSignatures
        }
      ]
    }
  ];

  readonly currentUser = this.authService.user;
  readonly userName = computed(() => this.currentUser()?.name ?? "");
  readonly status = signal<ApplicationStatus | undefined>(undefined);
  readonly applicationStatusUnavailable = signal(false);

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly authService: FinzzleAuthService,
    private readonly actionAuthorizationService: ActionAuthorizationService,
    private readonly apiService: DoublonsBusterApiService,
  ) {
    this.document.body.classList.add("theme-placement");
    this.document.body.classList.add("oui-typography");

    effect(
      (onCleanup) => {
        // userId (chaîne) et non user() : le compte MSAL est recréé à chaque événement MSAL,
        // ce qui relancerait l'effet (et ses appels API) en boucle.
        if (!this.authService.userId()) {
          this.status.set(undefined);
          this.actionAuthorizationService.clear();
          return;
        }

        this.actionAuthorizationService.load();

        const subscription = this.apiService.getApplicationStatus().subscribe({
          next: (status) => {
            this.applicationStatusUnavailable.set(false);
            this.status.set(status);
          },
          error: () => {
            this.applicationStatusUnavailable.set(true);
            this.status.set(undefined);
          },
        });

        onCleanup(() => subscription.unsubscribe());
      },
      { allowSignalWrites: true }
    );
  }

  logout(): void {
    void this.authService.logout();
  }
}
